import React, {
  useCallback,
  useState,
  useRef,
  useEffect,
  ComponentPropsWithoutRef,
  ElementType,
} from 'react';
import classnames from 'classnames';

import { ReactComponent as ChevronDownSVG } from '../../../../assets/icons/chevron-down.svg';
import Checkbox, { CheckboxSize } from '../../controls/checkbox/Checkbox';
import TimeInput from '../../controls/time-input/TimeInput';

import './Hours.scss';

export interface HoursValue {
  from: string;
  to: string;
  allDay: boolean;
}

export interface HoursProps extends ComponentPropsWithoutRef<ElementType> {
  className: string;
  value: HoursValue;
  onChange: (value: HoursValue) => void;
}

const Hours = ({
  className,
  value = { from: '', to: '', allDay: false },
  onChange,
}: HoursProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [innerValue, setInnerValue] = useState(value);
  const [menuPosition, setMenuPosition] = useState({ left: 0, top: 0 });

  useEffect(() => {
    setInnerValue((prevValue) => ({
      ...prevValue,
      from: value.from,
      to: value.to,
      allDay: value.allDay,
    }));
  }, [value.from, value.to, value.allDay]);

  const toggleMenu = useCallback(() => {
    if (!isOpen) {
      if (containerRef.current) {
        const containerBoundingRect =
          containerRef.current.getBoundingClientRect();
        setMenuPosition({
          top: containerBoundingRect.top + containerBoundingRect.height,
          left: containerBoundingRect.left,
        });
      }

      const closeMenuOnScroll = () => {
        setIsOpen(false);

        document.removeEventListener('scroll', closeMenuOnScroll, true);
      };

      document.addEventListener('scroll', closeMenuOnScroll, true);
    }

    setIsOpen(!isOpen);
  }, [isOpen]);

  useEffect(() => {
    const handleOutsideClick = ({ target }) => {
      if (containerRef.current && !containerRef.current.contains(target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('click', handleOutsideClick);

    return () => document.removeEventListener('click', handleOutsideClick);
  }, []);

  const handleFromTimeChange = useCallback(
    (_, value) => {
      const newInnerValue = {
        from: value,
        to: value ? innerValue.to || '24:00' : '',
        allDay: value ? false : innerValue.allDay,
      };

      setInnerValue(newInnerValue);

      if (onChange) {
        onChange(newInnerValue);
      }
    },
    [innerValue, onChange],
  );

  const handleToTimeChange = useCallback(
    (_, value) => {
      const newInnerValue = {
        from: value ? innerValue.from || '00:00' : '',
        to: value,
        allDay: value ? false : innerValue.allDay,
      };

      setInnerValue(newInnerValue);
      if (onChange) {
        onChange(newInnerValue);
      }
    },
    [innerValue, onChange],
  );

  const handleAllDayChange = useCallback(
    ({ target: { checked } }) => {
      const newInnerValue = {
        from: checked ? '' : innerValue.from,
        to: checked ? '' : innerValue.to,
        allDay: checked,
      };

      setInnerValue(newInnerValue);
      if (onChange) {
        onChange(newInnerValue);
      }
    },
    [innerValue, onChange],
  );

  let label;

  if (innerValue.allDay) {
    label = '24 hours';
  } else if (innerValue.from && innerValue.to) {
    label = `${innerValue.from} - ${innerValue.to}`;
  } else {
    label = 'Hours';
  }

  return (
    <div className={classnames('hours', className)} ref={containerRef}>
      <div
        className={classnames('hours__toggle', {
          'hours__toggle_has-value':
            innerValue.from || innerValue.to || innerValue.allDay,
        })}
        onClick={toggleMenu}
      >
        <div className="hours__label">{label}</div>
        <div className="hours__toggle-caret">
          <ChevronDownSVG />
        </div>
      </div>
      {isOpen && (
        <div
          className="hours__menu"
          style={{
            top: `${menuPosition.top}px`,
            left: `${menuPosition.left}px`,
          }}
        >
          <div className="hours__time-container">
            <div className="hours__from-container">
              <label className="hours__from-label">From</label>
              <TimeInput
                className="hours__from"
                disabled={innerValue.allDay}
                value={innerValue.from}
                onChange={handleFromTimeChange}
              />
            </div>
            <div className="hours__to-container">
              <label className="hours__to-label">to</label>
              <TimeInput
                className="hours__to"
                disabled={innerValue.allDay}
                value={innerValue.to}
                onChange={handleToTimeChange}
              />
            </div>
          </div>
          <label className="hours__all-day-toggle-container">
            <Checkbox
              className="hours__all-day-toggle"
              size={CheckboxSize.large}
              checked={innerValue.allDay}
              onChange={handleAllDayChange}
            />
            All day
          </label>
        </div>
      )}
    </div>
  );
};

export default Hours;
